// src/services/azureTimelinesService.js
// Azure Table Storage Service für Terminpläne (Metadaten).
// Spiegelt bewusst azureCostEstimatesService.js (Liste/Duplizieren/Soft-Delete/
// Editor-Tracking), damit das Termine-Tool dieselbe UX wie Kosten hat.
// Der eigentliche Plan-Inhalt (Tasks/Gruppen/Zuständige/Settings) liegt in der
// Tabelle `timelineItems` und wird vom timelineItemsProvider verwaltet.
import { ensureTableExists } from "./azureTableUtils";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "timelines",
  // Sicherheitshinweis: hartcodierter SAS-Token mit Vollrechten (rwdlacu) im
  // Frontend-Bundle – siehe ADR 0008. Mittelfristig auf Backend-Proxy umstellen.
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

// Alle Terminpläne laden (Soft-gelöschte ausgefiltert).
export const getAllTimelines = async () => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    let all = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      all = all.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return all
      .map((item) => ({
        id: item.RowKey,
        name: item.Name || "",
        linkedProjectCode: item.LinkedProjectCode || "",
        createdBy: item.CreatedBy || "",
        createdAt: item.CreatedAt || "",
        updatedAt: item.UpdatedAt || "",
        deletedAt: item.DeletedAt || "",
        editors: item.Editors ? JSON.parse(item.Editors) : [],
        // Pro-Plan freigegebene Bearbeiter:innen: [{uuid, teamId, name}]
        allowedEditors: item.AllowedEditors ? JSON.parse(item.AllowedEditors) : [],
      }))
      .filter((t) => !t.deletedAt);
  } catch (error) {
    console.error("Load Timelines Error:", error);
    return [];
  }
};

// Neuen Terminplan anlegen.
export const saveTimeline = async (timelineData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const timelineId = crypto.randomUUID();
    const now = new Date().toISOString();

    const allowedEditors = timelineData.allowedEditors || [];
    const entity = {
      PartitionKey: "MF",
      RowKey: timelineId,
      Name: cleanValue(timelineData.name),
      LinkedProjectCode: cleanValue(timelineData.linkedProjectCode),
      CreatedBy: cleanValue(timelineData.createdBy),
      CreatedAt: now,
      // Ersteller:innen ausserhalb GL/PL werden direkt als Bearbeiter:in eingetragen.
      AllowedEditors: JSON.stringify(allowedEditors),
    };

    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: timelineId,
      name: timelineData.name,
      linkedProjectCode: timelineData.linkedProjectCode || "",
      createdBy: timelineData.createdBy || "",
      createdAt: now,
      allowedEditors,
    };
  } catch (error) {
    console.error("Save Timeline Error:", error);
    throw error;
  }
};

// Terminplan-Metadaten aktualisieren.
// MERGE statt PUT, damit Editors (trackEditor) und DeletedAt erhalten bleiben.
export const updateTimeline = async (timelineId, timelineData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "MF",
      RowKey: timelineId,
      Name: cleanValue(timelineData.name),
      LinkedProjectCode: cleanValue(timelineData.linkedProjectCode),
      CreatedBy: cleanValue(timelineData.createdBy),
      CreatedAt: cleanValue(timelineData.createdAt),
      AllowedEditors: JSON.stringify(timelineData.allowedEditors || []),
      UpdatedAt: now,
    };

    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return { id: timelineId, ...timelineData, updatedAt: now };
  } catch (error) {
    console.error("Update Timeline Error:", error);
    throw error;
  }
};

// Nur den UpdatedAt-Zeitstempel hochsetzen (z.B. nach Inhalts-Speicherung im Editor).
export const touchTimeline = async (timelineId) => {
  try {
    const now = new Date().toISOString();
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;
    await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify({ PartitionKey: "MF", RowKey: timelineId, UpdatedAt: now }),
    });
  } catch (error) {
    console.error("Touch Timeline Error:", error);
  }
};

// Nur die Bearbeiter:innen-Freigabe eines Plans setzen (GL/PL).
// MERGE schreibt ausschliesslich AllowedEditors, damit andere Felder erhalten bleiben.
export const updateAllowedEditors = async (timelineId, allowedEditors) => {
  try {
    const now = new Date().toISOString();
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify({
        PartitionKey: "MF",
        RowKey: timelineId,
        AllowedEditors: JSON.stringify(allowedEditors || []),
        UpdatedAt: now,
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }
  } catch (error) {
    console.error("Update AllowedEditors Error:", error);
    throw error;
  }
};

// Soft-Delete (30 Tage Aufbewahrung, wie bei Kosten-Dossiers).
export const softDeleteTimeline = async (timelineId) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify({ PartitionKey: "MF", RowKey: timelineId, DeletedAt: now }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }
  } catch (error) {
    console.error("Soft Delete Timeline Error:", error);
    throw error;
  }
};

// Terminplan-Metadaten duplizieren (Inhalt kopiert der Aufrufer via Provider).
export const duplicateTimeline = async (sourceTimeline, createdBy) => {
  try {
    const saved = await saveTimeline({
      name: `${sourceTimeline.name} (Kopie)`,
      linkedProjectCode: sourceTimeline.linkedProjectCode,
      createdBy,
    });
    return saved;
  } catch (error) {
    console.error("Duplicate Timeline Error:", error);
    throw error;
  }
};

// Editor-Aktivität auf einem Terminplan vermerken (für Avatare in der Liste).
export const trackEditor = async (timelineId, userName) => {
  if (!userName) return;
  try {
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${timelineId}')?${AZURE_CONFIG.sasToken}`;
    const getResp = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!getResp.ok) return;

    const entity = await getResp.json();
    const editors = entity.Editors ? JSON.parse(entity.Editors) : [];
    const now = new Date().toISOString();
    const existing = editors.findIndex((e) => e.name === userName);
    if (existing >= 0) {
      editors[existing].lastActive = now;
    } else {
      editors.push({ name: userName, lastActive: now });
    }

    await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify({
        PartitionKey: "MF",
        RowKey: timelineId,
        Editors: JSON.stringify(editors),
        UpdatedAt: now,
      }),
    });
  } catch (error) {
    console.error("Track editor error:", error);
  }
};
